import React from 'react';

import { ConfigurationDto } from 'lib_api/lib/api/gen';

import TableQuery from 'components/WrappedComponents/Table/TableQuery/TableQuery';
import {
  ColumnProps,
  TableHeaderFunction,
} from 'components/WrappedComponents/Table/types';
import { useDashboardSearchQuery } from 'hooks/search/useDashboardSearchQuery';
import { DEFAULT_PAGE_SIZE_OPTIONS } from 'utils/global';

import CellDetailsAndActions from './CellDetailsAndActions/CellDetailsAndActions';
import CellParsingError from './CellParsingError/CellParsingError';
import DashboardConfigurationHeader from './DashboardHeader/DashboardConfigurationHeader';
import { configuration_labels, configurationQueryKeyLabel } from './labels';
import {
  ConfigurationSortOptions,
  DashboardConfigurationFilter,
} from './types';
import { useFetchConfigurationQuery } from './useFetchConfiguration';

function DashboardConfigurationTable(): React.ReactElement {
  const buildColumns = (): ColumnProps<
    ConfigurationDto,
    ConfigurationSortOptions
  >[] => {
    return [
      {
        title: '',
        buildCellContent: (data: ConfigurationDto) => {
          if (data.parsingError) {
            return <CellParsingError configuration={data} />;
          }
        },
      },
      {
        title: configuration_labels.SERVICE,
        buildCellContent: (data: ConfigurationDto) => data.microServiceName,
        sortedField: ConfigurationSortOptions.SERVICE,
      },
      {
        title: configuration_labels.NOM,
        buildCellContent: (data: ConfigurationDto) => data.fullName,
      },
      {
        title: configuration_labels.DESCRIPTION,
        buildCellContent: (data: ConfigurationDto) => data.description,
      },
      {
        title: configuration_labels.VALEUR,
        buildCellContent: (data: ConfigurationDto) => data.value,
      },
      {
        title: '',
        buildCellContent: (data: ConfigurationDto) => (
          <CellDetailsAndActions configuration={data} />
        ),
      },
    ];
  };

  const buildHeader: TableHeaderFunction<
    ConfigurationDto,
    DashboardConfigurationFilter
  > = (fetchData, _selectedData, filter, setFilter) => {
    return (
      <DashboardConfigurationHeader
        filter={filter}
        setFilter={setFilter}
        totalResult={fetchData?.total ?? 0}
      />
    );
  };

  const initialFilter: DashboardConfigurationFilter = {
    search: undefined,
    microServiceName: undefined,
    value: undefined,
    hasError: undefined,
  };
  const { query, filter, setFilter, sort, setSort, pagination, setPagination } =
    useDashboardSearchQuery(
      useFetchConfigurationQuery,
      configurationQueryKeyLabel,
      initialFilter,
      {
        SERVICE: true,
      },
      'paginationConfiguration',
    );

  return (
    <div className="table-container">
      <TableQuery
        columns={buildColumns()}
        query={query}
        queryKeyLabel={configurationQueryKeyLabel}
        header={buildHeader}
        pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
        pagination={pagination}
        setPagination={setPagination}
        filter={filter}
        setFilter={setFilter}
        sort={sort}
        setSort={setSort}
        buildItemKey={(config: ConfigurationDto) => config.id}
      />
    </div>
  );
}

export default DashboardConfigurationTable;
